/**
 * ResidentCard.jsx
 * CCH Care — Phase 2 — Layer 3
 * Last Modified: 2026-04-21
 *
 * Displays a single resident in list or grid view.
 * Shows: name, room, alerts, ADL progress, last interaction, quick action.
 *
 * Changes (2026-04-21):
 * - Dedupe: ALERT_BADGE_COLORS removed. Now imports ALERT_BADGES from
 *   constants.js — single source of truth shared with
 *   ResidentsDirectoryPage.jsx.
 */
import { COLORS, ALERT_BADGES } from '../../../config/constants';

const STATUS_BORDER = {
  overdue:     COLORS.danger,
  in_progress: COLORS.warning,
  complete:    COLORS.accent,
  not_started: 'transparent',
};

export default function ResidentCard({ resident, viewMode, onTap, onStartADL }) {
  const r = resident;
  const isOverdue  = r.status === 'overdue';
  const isComplete = r.status === 'complete';
  const percent    = r.adl_percent || 0;
  const progressColor = percent >= 70 ? COLORS.accent : percent >= 30 ? COLORS.warning : COLORS.danger;
  const borderColor   = STATUS_BORDER[r.status] || 'transparent';

  const timeAgo = r.minutes_since_last !== null
    ? r.minutes_since_last < 60
      ? `${r.minutes_since_last}m ago`
      : `${Math.floor(r.minutes_since_last / 60)}h ${r.minutes_since_last % 60}m ago`
    : null;

  if (viewMode === 'grid') {
    return (
      <div
        role="button"
        tabIndex={0}
        onClick={onTap}
        onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && onTap()}
        style={{
          background: COLORS.surface,
          borderRadius: 12,
          border: `2px solid ${borderColor}`,
          padding: 14,
          cursor: 'pointer',
          transition: 'box-shadow 0.15s',
          position: 'relative',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
          <Avatar initials={r.initials} isOverdue={isOverdue} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {r.full_name}
            </div>
            <div style={{ fontSize: 12, color: COLORS.textMuted }}>
              Rm {r.room_number}{r.bed_label ? ` · ${r.bed_label}` : ''}
            </div>
          </div>
          {r.is_new_admit && <NewBadge />}
        </div>

        {r.alerts?.length > 0 && (
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 8 }}>
            {r.alerts.map((a, i) => <AlertBadge key={i} alert={a} />)}
          </div>
        )}

        <div style={{ marginBottom: 6 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
            <span style={{ fontSize: 11, color: COLORS.textMuted }}>ADL</span>
            <span style={{ fontSize: 11, fontWeight: 700, color: progressColor }}>
              {r.adl_done}/{r.adl_total}
            </span>
          </div>
          <ProgressBar percent={percent} color={progressColor} />
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 11, color: isOverdue ? COLORS.danger : COLORS.textMuted, fontWeight: isOverdue ? 600 : 400 }}>
            {timeAgo || '—'}
          </span>
          {!isComplete && (
            <button
              onClick={e => { e.stopPropagation(); onStartADL(); }}
              style={startBtnStyle}
            >
              ▶ Start
            </button>
          )}
        </div>
      </div>
    );
  }

  // ── List view ──
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onTap}
      onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && onTap()}
      style={{
        background: COLORS.surface,
        borderBottom: `1px solid ${COLORS.border}`,
        borderLeft: `4px solid ${borderColor}`,
        padding: '12px 16px 12px 12px',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        cursor: 'pointer',
      }}
    >
      <Avatar initials={r.initials} isOverdue={isOverdue} />

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: COLORS.text }}>{r.full_name}</span>
          <span style={{ fontSize: 12, color: COLORS.textMuted }}>Rm {r.room_number}</span>
          {r.is_new_admit && <NewBadge />}
        </div>

        {r.alerts?.length > 0 && (
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 4 }}>
            {r.alerts.map((a, i) => <AlertBadge key={i} alert={a} />)}
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ flex: 1, maxWidth: 120 }}>
            <ProgressBar percent={percent} color={progressColor} />
          </div>
          <span style={{ fontSize: 11, fontWeight: 600, color: progressColor }}>
            {r.adl_done}/{r.adl_total}
          </span>
          {timeAgo && (
            <span style={{ fontSize: 11, color: isOverdue ? COLORS.danger : COLORS.textMuted, fontWeight: isOverdue ? 600 : 400 }}>
              {timeAgo}
            </span>
          )}
        </div>
      </div>

      {!isComplete && (
        <button
          onClick={e => { e.stopPropagation(); onStartADL(); }}
          style={startBtnStyle}
        >
          ▶
        </button>
      )}
      {isComplete && (
        <span style={{ fontSize: 18, color: COLORS.accent }}>✓</span>
      )}
    </div>
  );
}

function Avatar({ initials, isOverdue }) {
  return (
    <div style={{
      width: 40, height: 40, borderRadius: '50%',
      background: isOverdue ? COLORS.dangerLight : COLORS.primaryLight,
      color: isOverdue ? COLORS.danger : COLORS.primary,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 14, fontWeight: 700, flexShrink: 0,
    }}>
      {initials}
    </div>
  );
}

function AlertBadge({ alert }) {
  const config = ALERT_BADGES[alert.alert_type] || ALERT_BADGES.custom;
  return (
    <span style={{
      padding: '2px 6px', borderRadius: 4, fontSize: 10, fontWeight: 700,
      background: config.bg, color: config.text, whiteSpace: 'nowrap',
    }}>
      {alert.alert_label || config.label}
    </span>
  );
}

function NewBadge() {
  return (
    <span style={{
      padding: '1px 6px', borderRadius: 4, fontSize: 9, fontWeight: 700,
      background: COLORS.primaryLight, color: COLORS.primary, textTransform: 'uppercase',
    }}>
      New
    </span>
  );
}

function ProgressBar({ percent, color }) {
  return (
    <div style={{ height: 4, borderRadius: 2, background: COLORS.bg, overflow: 'hidden' }}>
      <div style={{ width: `${percent}%`, height: '100%', borderRadius: 2, background: color, transition: 'width 0.3s ease' }} />
    </div>
  );
}

const startBtnStyle = {
  width: 36, height: 36, borderRadius: '50%',
  border: 'none', background: COLORS.primary, color: '#fff',
  fontSize: 14, cursor: 'pointer',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  flexShrink: 0,
};